import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import {
  isArrayVariable,
  isNumberVariable,
  normalizeArrayValues,
} from '../../../../utils/variableScope';
import { variableTypeOf } from '../../../../utils/variableTypeChange';
import {
  inspectPathValue,
  pinPathSeparators,
  planPathSeparatorFixes,
} from '../../../../utils/pathSeparators';

const UNDO_MS = 12000;

let offerSeq = 1;

// The variables table can re-render from the value write before a useState
// update in this hook is painted. The chip has to survive that commit.
let slashSnapshot = { offers: [], paintEpoch: 0 };
const slashListeners = new Set();

const emitSlash = (next) => {
  slashSnapshot = next;
  slashListeners.forEach((listener) => listener());
};

const subscribeSlash = (listener) => {
  slashListeners.add(listener);
  return () => slashListeners.delete(listener);
};

export const resetPathSlashOffers = () => {
  offerSeq = 1;
  emitSlash({ offers: [], paintEpoch: 0 });
};

const hasValueKey = (patch) =>
  !!patch &&
  typeof patch === 'object' &&
  (Object.prototype.hasOwnProperty.call(patch, 'value') ||
    Object.prototype.hasOwnProperty.call(patch, 'values'));

const slotsOf = (variable) => {
  if (!variable || isNumberVariable(variable)) return [];
  if (isArrayVariable(variable)) {
    return normalizeArrayValues(variable.values).map((value, slot) => ({
      slot,
      value: String(value ?? ''),
    }));
  }
  return [{ slot: null, value: String(variable.value ?? '') }];
};

const groupKey = (scope, id) => `${scope}\0${id}`;

const applyFixes = (variable, patch, fixes) => {
  if (!fixes.length) return patch;
  const merged = { ...variable, ...patch };
  if (isArrayVariable(merged)) {
    const values = normalizeArrayValues(
      Object.prototype.hasOwnProperty.call(patch, 'values') ? patch.values : variable.values
    ).slice();
    for (const fix of fixes) {
      if (fix.slot != null) values[fix.slot] = fix.next;
    }
    return { ...patch, values, value: patch.value ?? variable.value ?? '' };
  }
  const fix = fixes.find((entry) => entry.slot == null) || fixes[0];
  return { ...patch, value: fix.next };
};

/**
 * On a path-value commit, snap mixed or inconsistent slashes to one
 * separator and keep a short Undo for each value that changed.
 */
export default function usePathSlashAutocorrect({ displayedRef, onPatch }) {
  const onPatchRef = useRef(onPatch);
  onPatchRef.current = onPatch;
  const { offers, paintEpoch } = useSyncExternalStore(
    subscribeSlash,
    () => slashSnapshot
  );

  useEffect(() => {
    if (offers.length === 0) return undefined;
    const soonest = Math.min(...offers.map((offer) => offer.expiresAt));
    const timer = setTimeout(() => {
      const current = slashSnapshot.offers;
      const next = current.filter((offer) => offer.expiresAt > Date.now());
      if (next.length !== current.length) {
        emitSlash({ ...slashSnapshot, offers: next });
      }
    }, Math.max(0, soonest - Date.now()));
    return () => clearTimeout(timer);
  }, [offers]);

  const prepare = useCallback((scope, id, patch) => {
    const empty = { patch, extras: [], offers: [] };
    if (!hasValueKey(patch) || Object.prototype.hasOwnProperty.call(patch, 'name')) {
      return empty;
    }

    const displayed = displayedRef.current || [];
    const edited = displayed.find(
      (entry) => entry.scope === scope && entry.variable?.id === id
    );
    if (!edited) return empty;

    if (Object.prototype.hasOwnProperty.call(patch, 'type')) {
      const current = variableTypeOf(edited.variable);
      if (patch.type !== current) return empty;
    }

    const before = edited.variable;
    const after = { ...before, ...patch };
    const beforeSlots = slotsOf(before);
    const afterSlots = slotsOf(after);
    const count = Math.max(beforeSlots.length, afterSlots.length);
    let touchesPath = false;
    for (let i = 0; i < count; i += 1) {
      const prev = beforeSlots[i]?.value ?? '';
      const next = afterSlots[i]?.value ?? '';
      if (prev === next) continue;
      if (inspectPathValue(prev) || inspectPathValue(next)) touchesPath = true;
    }
    if (!touchesPath) return empty;

    const flat = [];
    for (const entry of displayed) {
      const isEdited = entry.scope === scope && entry.variable?.id === id;
      const variable = isEdited ? after : entry.variable;
      const writable = isEdited || (entry.writable !== false && !entry.locked && !variable?.locked);
      for (const slot of slotsOf(variable)) {
        flat.push({
          variableId: entry.variable.id,
          scope: entry.scope,
          slot: slot.slot,
          value: slot.value,
          writable,
          locked: !isEdited && (!!entry.locked || !!variable?.locked),
        });
      }
    }

    const fixes = planPathSeparatorFixes(flat);
    if (fixes.length === 0) return empty;

    const byKey = new Map();
    for (const fix of fixes) {
      const key = groupKey(fix.scope, fix.variableId);
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(fix);
    }

    const editedKey = groupKey(scope, id);
    const nextPatch = applyFixes(before, patch, byKey.get(editedKey) || []);
    const extras = [];
    for (const [key, group] of byKey) {
      if (key === editedKey) continue;
      const entry = displayed.find(
        (row) => groupKey(row.scope, row.variable?.id) === key
      );
      if (!entry) continue;
      extras.push({
        scope: entry.scope,
        id: entry.variable.id,
        patch: applyFixes(entry.variable, {}, group),
      });
    }

    const now = Date.now();
    const nextOffers = fixes.map((fix) => ({
      id: offerSeq++,
      scope: fix.scope,
      variableId: fix.variableId,
      slot: fix.slot,
      previous: fix.previous,
      next: fix.next,
      expiresAt: now + UNDO_MS,
    }));

    return { patch: nextPatch, extras, offers: nextOffers };
  }, [displayedRef]);

  const remember = useCallback((nextOffers, edited) => {
    if (!nextOffers?.length) return;
    const keys = new Set(
      nextOffers.map((offer) => `${offer.scope}\0${offer.variableId}\0${offer.slot ?? ''}`)
    );
    const merged = [
      ...slashSnapshot.offers.filter(
        (offer) => !keys.has(`${offer.scope}\0${offer.variableId}\0${offer.slot ?? ''}`)
      ),
      ...nextOffers,
    ];
    const bump = nextOffers.some(
      (offer) => offer.scope === edited?.scope && offer.variableId === edited?.id
    );
    emitSlash({
      offers: merged,
      paintEpoch: bump ? slashSnapshot.paintEpoch + 1 : slashSnapshot.paintEpoch,
    });
  }, []);

  const applyExtras = useCallback((extras) => {
    for (const extra of extras || []) {
      if (extra.scope === 'inherited') continue;
      onPatchRef.current(extra.scope, extra.id, extra.patch, {
        silent: true,
        skipPathSlash: true,
      });
    }
  }, []);

  const commitThrough = useCallback(
    (scope, id, patch, opts, route) => {
      if (opts?.skipPathSlash) {
        route(patch, opts);
        return;
      }
      const prepared = prepare(scope, id, patch);
      route(prepared.patch, opts);
      applyExtras(prepared.extras);
      remember(prepared.offers, { scope, id });
    },
    [applyExtras, prepare, remember]
  );

  const onPatchWrapped = useCallback(
    (scope, id, patch, opts) => {
      commitThrough(scope, id, patch, opts, (next, nextOpts) => {
        onPatchRef.current(scope, id, next, nextOpts);
      });
    },
    [commitThrough]
  );

  const undo = useCallback((offer, restoreInherited) => {
    if (!offer) return;
    pinPathSeparators(offer.variableId, offer.slot, offer.previous);
    emitSlash({
      ...slashSnapshot,
      offers: slashSnapshot.offers.filter((entry) => entry.id !== offer.id),
    });
    const displayed = displayedRef.current || [];
    const entry = displayed.find(
      (row) => row.scope === offer.scope && row.variable?.id === offer.variableId
    );
    const variable = entry?.variable;
    const patch =
      offer.slot == null
        ? { value: offer.previous }
        : {
            values: normalizeArrayValues(variable?.values).map((value, index) =>
              index === offer.slot ? offer.previous : value
            ),
          };
    if (offer.scope === 'inherited') {
      restoreInherited?.(offer, patch);
      return;
    }
    onPatchRef.current(offer.scope, offer.variableId, patch, {
      silent: false,
      skipPathSlash: true,
    });
  }, [displayedRef]);

  const liveOffers = offers.filter((offer) => {
    const entry = (displayedRef.current || []).find(
      (row) => row.scope === offer.scope && row.variable?.id === offer.variableId
    );
    const slot = slotsOf(entry?.variable).find((entrySlot) => entrySlot.slot === offer.slot);
    if (!entry) return false;
    return slot?.value === offer.next;
  });

  return {
    offers: liveOffers,
    paintEpoch,
    onPatch: onPatchWrapped,
    commitThrough,
    undo,
  };
};
